// Pure data helpers for convenio compliance views.
// Both the broker page (ConvenioDetails) and the client portal page
// (ClientPortalConvenio) consume these — keep this file free of React imports.

import { getEstadoFianza, ESTADO_FIANZA } from './fianzaEventos';

const MS_PER_DAY = 86400000;

// Mexican legal-entity suffixes stripped during name comparison.
// Longer forms first so the loop peels them in the right order.
const LEGAL_SUFFIXES = [
  'sa bursatil de cv',
  'sapi de cv',
  's de rl de cv',
  'srl de cv',
  'sab de cv',
  'sofom enr',
  'sofom er',
  'sa de cv',
  's de rl',
  'sapi',
  'srl',
  'sab',
  'sa',
  'sc',
  'ac',
];

// Normalize a company name for fuzzy comparison: strip accents, punctuation,
// collapse whitespace, peel legal-entity suffixes off the end.
export function normalizeName(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let s = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[.,;:'"`´]/g, '')
    .replace(/[/\\\-_()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  let changed = true;
  while (changed) {
    changed = false;
    for (const suf of LEGAL_SUFFIXES) {
      if (s === suf) { s = ''; changed = true; break; }
      if (s.endsWith(' ' + suf)) {
        s = s.slice(0, s.length - suf.length - 1).trim();
        changed = true;
        break;
      }
    }
  }
  return s;
}

export function namesMatch(a, b) {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return true;
  return na === nb;
}

// Returns null if there is no contradiction, otherwise the divergent values.
export function getProveedorMismatch(contractProveedor, xmlFiadoNombre) {
  if (!contractProveedor || !xmlFiadoNombre) return null;
  if (namesMatch(contractProveedor, xmlFiadoNombre)) return null;
  return { contractProveedor, fiadoXmlNombre: xmlFiadoNombre };
}

export function parseContractDate(str) {
  if (!str || typeof str !== 'string') return null;
  const m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!m) return null;
  const dt = new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
  return isNaN(dt.getTime()) ? null : dt.getTime();
}

// Shared display helpers for pendiente requirements. They live here so the
// Proveedores cards and the Contratos tab speak the same vocabulary about the
// same requirement — one string, one place.
export function formatFechaCorta(ts) {
  if (!ts) return '-';
  const ms = ts?.toMillis?.() || ts;
  return new Date(ms).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function dueLabel(fr) {
  if (fr.dueDate == null) return 'Sin fecha definida';
  if (fr.isOverdue) {
    if (fr.daysOverdue === 0) return 'Atrasada hoy';
    return `Atrasada ${fr.daysOverdue} día${fr.daysOverdue !== 1 ? 's' : ''}`;
  }
  if (fr.daysUntilDue === 0) return 'Vence hoy';
  if (fr.daysUntilDue <= 30) return `Vence en ${fr.daysUntilDue} día${fr.daysUntilDue !== 1 ? 's' : ''}`;
  return formatFechaCorta(fr.dueDate);
}

// Canonical fianza status. Returns { label, color, code, estado }. The `label`
// and `color` contract is preserved for existing callers; `code` is the
// lowercase status token ('vigente'|'vencida'|'pendiente'|'cancelada'|'anulada'
// |'sustituida') used by the list/filter surfaces. Terminal lifecycle states
// (cancelada / anulada / sustituida) take precedence over the date-derived
// status.
export function getFianzaStatus(f, nowMs) {
  const now = nowMs ?? Date.now();
  const estado = getEstadoFianza(f);
  if (estado === ESTADO_FIANZA.ANULADA) {
    return { label: 'Anulada', color: 'zinc', code: 'anulada', estado };
  }
  if (estado === ESTADO_FIANZA.CANCELADA) {
    return { label: 'Cancelada', color: 'zinc', code: 'cancelada', estado };
  }
  if (estado === ESTADO_FIANZA.SUSTITUIDA) {
    return { label: 'Sustituida', color: 'zinc', code: 'sustituida', estado };
  }
  const fin = f?.finVigenciaFianza?.toMillis?.() || f?.finVigenciaFianza || 0;
  const inicio = f?.inicioVigenciaFianza?.toMillis?.() || f?.inicioVigenciaFianza || 0;
  if (fin && fin < now) return { label: 'Vencida', color: 'red', code: 'vencida', estado };
  if (inicio && inicio <= now) return { label: 'Vigente', color: 'green', code: 'vigente', estado };
  return { label: 'Pendiente', color: 'zinc', code: 'pendiente', estado };
}

function computeDueDate(fr, fechaInicio, fechaTermino) {
  if (fr.postergadaHasta) {
    const p = fr.postergadaHasta?.toMillis?.() || fr.postergadaHasta;
    return typeof p === 'number' ? p : null;
  }
  if (fr.momento === 'antes_termino' || fr.momento === 'despues_termino') {
    return fechaTermino;
  }
  return fechaInicio;
}

function enrichPending(fr, idx, contract, beneficiariosById, proyectosById, nowMs) {
  const fechaInicio = parseContractDate(contract.fechaInicio?.value);
  const fechaTermino = parseContractDate(contract.fechaTermino?.value);
  const dueDate = computeDueDate(fr, fechaInicio, fechaTermino);
  const isOverdue = dueDate != null && dueDate < nowMs;
  const daysOverdue = isOverdue ? Math.floor((nowMs - dueDate) / MS_PER_DAY) : 0;
  const daysUntilDue = !isOverdue && dueDate != null
    ? Math.floor((dueDate - nowMs) / MS_PER_DAY)
    : null;
  // Requirements like 'vicios ocultos' that only apply after the contract ends —
  // they're pendientes but shouldn't read as urgent.
  const isFutura = fr.momento === 'despues_termino';
  return {
    ...fr,
    _key: `${contract.id}_req_${idx}`,
    contractId: contract.id,
    contractTitle: contract.tituloContrato?.value || 'Sin título',
    contractNumero: contract.numeroContrato?.value || null,
    beneficiarioId: contract.beneficiarioId,
    beneficiarioNombre: beneficiariosById[contract.beneficiarioId]?.nombre || '',
    proyectoId: contract.proyectoId || null,
    proyectoNombre: proyectosById[contract.proyectoId]?.nombre || '',
    fiadoNombre: contract.proveedor?.value || '',
    dueDate,
    isOverdue,
    daysOverdue,
    daysUntilDue,
    isFutura,
  };
}

// The share of the CONTRATO that a fianza covers. It is authored on the
// requisito (fianzasRequeridas[].porcentaje — "% del contrato"), never on the
// emitted fianza, so an emitted fianza reads it off the requisito that points
// at it. When no requisito authored one (fianza uploaded without a matching
// requirement), derive it from montoAfianzado / montoContrato and mark it so
// the UI can say it is calculated rather than declared.
export function getFianzaPorcentaje(fianza, contract) {
  const reqs = contract?.fianzasRequeridas?.value || [];
  const req = reqs.find((fr) => fr.fianzaId && fianza?.id && fr.fianzaId === fianza.id);
  const authored = req?.porcentaje;
  if (authored !== undefined && authored !== null && authored !== '') {
    const n = Number(authored);
    if (!Number.isNaN(n) && n > 0) return { pct: n, derived: false };
  }
  const montoContrato = Number(contract?.montoContrato?.value) || 0;
  const monto = Number(fianza?.montoAfianzado) || 0;
  if (montoContrato > 0 && monto > 0) {
    return { pct: Math.round((monto / montoContrato) * 1000) / 10, derived: true };
  }
  return null;
}

function enrichEmitted(f, contract, beneficiariosById, proyectosById) {
  const contractProveedor = contract.proveedor?.value || '';
  const fiadoXmlNombre = f.xmlFianza?.fiado?.nombre || '';
  return {
    ...f,
    contractId: contract.id,
    contractTitle: contract.tituloContrato?.value || 'Sin título',
    contractNumero: contract.numeroContrato?.value || null,
    beneficiarioId: contract.beneficiarioId,
    beneficiarioNombre: beneficiariosById[contract.beneficiarioId]?.nombre || '',
    proyectoId: contract.proyectoId || null,
    proyectoNombre: proyectosById[contract.proyectoId]?.nombre || '',
    contractProveedor,
    fiadoXmlNombre,
    proveedorMismatch: getProveedorMismatch(contractProveedor, fiadoXmlNombre),
    porcentajeInfo: getFianzaPorcentaje(f, contract),
  };
}

export function computePendingFianzas(contracts, beneficiariosById = {}, proyectosById = {}) {
  const nowMs = Date.now();
  const result = [];
  (contracts || []).forEach((contract) => {
    (contract.fianzasRequeridas?.value || []).forEach((fr, idx) => {
      if (fr.fianzaId) return;
      result.push(enrichPending(fr, idx, contract, beneficiariosById, proyectosById, nowMs));
    });
  });
  return result;
}

export function computeEmittedFianzas(contracts, beneficiariosById = {}, proyectosById = {}) {
  const result = [];
  (contracts || []).forEach((contract) => {
    (contract.fianzas || []).forEach((f) => {
      result.push(enrichEmitted(f, contract, beneficiariosById, proyectosById));
    });
  });
  return result;
}

// Resolve fiado identity for a contract. Contract data prevails: the proveedor
// name on the contract is the source of truth (always a name, no RFC). RFC,
// when available, comes from the fianza XML for supplementary display only.
function resolveFiadoForContract(contract) {
  const nombre = contract.proveedor?.value || null;
  let rfc = null;
  (contract.fianzas || []).forEach((f) => {
    if (!rfc && f.xmlFianza?.fiado?.rfc) rfc = f.xmlFianza.fiado.rfc;
  });
  return { nombre, rfc };
}

// `pendiente` is the TOTAL of requirements without a fianza, and it splits into
// two DISJOINT halves: `porEmitir` (emisión ya exigible o próxima) and `futura`
// (momento === 'despues_termino', p. ej. vicios ocultos — no se puede emitir
// hasta que el contrato termine). Display surfaces must show porEmitir/futura
// side by side, never pendiente next to futura: that double-counts the same
// requirement. `overdue` and `dueSoon` are a separate, orthogonal cut of
// pendiente driven by dueDate, so they may overlap either half.
// Los montos de un requisito pueden venir como string: el editor de requisitos
// de ContratoView guarda `e.target.value` tal cual, mientras que la extracción
// guarda números nativos. `x || 0` NO alcanza — un string no vacío es truthy,
// así que pasa entero y `+=` deja de sumar para concatenar, volviendo NaN todo
// el rollup del proveedor. Coercionar aquí es lo único que arregla los datos ya
// guardados.
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function emptyAggregate(key, label, rfc) {
  return {
    key,
    label,
    rfc,
    contractIds: new Set(),
    pendientes: [],
    emitidas: [],
    counts: { pendiente: 0, porEmitir: 0, vigente: 0, vencida: 0, overdue: 0, dueSoon: 0, futura: 0 },
    montos: { total: 0, pendiente: 0, porEmitir: 0, vigente: 0, overdue: 0, vencida: 0, futura: 0 },
    oldestOverdueDate: null,
  };
}

export function aggregateProveedores(contracts, beneficiariosById = {}, proyectosById = {}) {
  const nowMs = Date.now();
  const map = new Map();

  (contracts || []).forEach((contract) => {
    const { nombre, rfc } = resolveFiadoForContract(contract);
    if (!nombre && !rfc) return;
    const normalized = normalizeName(nombre);
    const key = normalized ? `name:${normalized}` : '__sin_proveedor__';
    const label = nombre || 'Sin proveedor';
    if (!map.has(key)) map.set(key, emptyAggregate(key, label, rfc));
    const g = map.get(key);
    if (!g.rfc && rfc) g.rfc = rfc;
    g.contractIds.add(contract.id);

    // Pending
    (contract.fianzasRequeridas?.value || []).forEach((fr, idx) => {
      if (fr.fianzaId) return;
      const enriched = enrichPending(fr, idx, contract, beneficiariosById, proyectosById, nowMs);
      g.pendientes.push(enriched);
      g.counts.pendiente++;
      g.montos.total += num(enriched.monto);
      g.montos.pendiente += num(enriched.monto);
      if (enriched.isFutura) {
        g.counts.futura++;
        g.montos.futura += num(enriched.monto);
      } else {
        g.counts.porEmitir++;
        g.montos.porEmitir += num(enriched.monto);
      }
      if (enriched.isOverdue) {
        g.counts.overdue++;
        g.montos.overdue += num(enriched.monto);
        if (g.oldestOverdueDate == null || enriched.dueDate < g.oldestOverdueDate) {
          g.oldestOverdueDate = enriched.dueDate;
        }
      } else if (enriched.daysUntilDue != null && enriched.daysUntilDue <= 30) {
        g.counts.dueSoon++;
      }
    });

    // Emitted
    (contract.fianzas || []).forEach((f) => {
      const enriched = enrichEmitted(f, contract, beneficiariosById, proyectosById);
      const status = getFianzaStatus(enriched, nowMs);
      g.emitidas.push({ ...enriched, _status: status });
      // Terminal fianzas (cancelada/anulada) carry no live exposure — keep them
      // listed but exclude from the monto rollups.
      if (status.estado !== ESTADO_FIANZA.ACTIVA) return;
      g.montos.total += num(enriched.montoAfianzado);
      if (status.label === 'Vigente') {
        g.counts.vigente++;
        g.montos.vigente += num(enriched.montoAfianzado);
      } else if (status.label === 'Vencida') {
        g.counts.vencida++;
        g.montos.vencida += num(enriched.montoAfianzado);
      }
    });
  });

  // Sort items within each group: pendientes overdue-first then by due date;
  // emitidas by inicio desc.
  map.forEach((g) => {
    g.pendientes.sort((a, b) => {
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
      if (a.dueDate == null && b.dueDate == null) return 0;
      if (a.dueDate == null) return 1;
      if (b.dueDate == null) return -1;
      return a.dueDate - b.dueDate;
    });
    g.emitidas.sort((a, b) => {
      const ai = a.inicioVigenciaFianza?.toMillis?.() || a.inicioVigenciaFianza || 0;
      const bi = b.inicioVigenciaFianza?.toMillis?.() || b.inicioVigenciaFianza || 0;
      return bi - ai;
    });
    g.contracts = Array.from(g.contractIds);
    delete g.contractIds;
  });

  // Sort proveedores by risk
  return Array.from(map.values()).sort((a, b) => {
    if (a.key === '__sin_proveedor__') return 1;
    if (b.key === '__sin_proveedor__') return -1;
    if (a.montos.overdue !== b.montos.overdue) return b.montos.overdue - a.montos.overdue;
    if (a.counts.overdue !== b.counts.overdue) return b.counts.overdue - a.counts.overdue;
    return b.montos.total - a.montos.total;
  });
}

export function computeConvenioTotals(proveedoresAgg) {
  const totals = {
    totalProveedores: proveedoresAgg.length,
    totalContratos: 0,
    fianzasVigentes: 0,
    fianzasVencidas: 0,
    fianzasPendientes: 0,
    fianzasPorEmitir: 0,
    fianzasFuturas: 0,
    montoTotal: 0,
    montoPendiente: 0,
    montoPorEmitir: 0,
    montoVencido: 0,
    montoFutura: 0,
  };
  const contractIds = new Set();
  proveedoresAgg.forEach((p) => {
    p.contracts.forEach((id) => contractIds.add(id));
    totals.fianzasVigentes += p.counts.vigente;
    totals.fianzasVencidas += p.counts.vencida;
    totals.fianzasPendientes += p.counts.pendiente;
    totals.fianzasPorEmitir += p.counts.porEmitir;
    totals.fianzasFuturas += p.counts.futura;
    totals.montoTotal += p.montos.total;
    totals.montoPendiente += p.montos.pendiente;
    totals.montoPorEmitir += p.montos.porEmitir;
    totals.montoVencido += p.montos.vencida;
    totals.montoFutura += p.montos.futura;
  });
  totals.totalContratos = contractIds.size;
  return totals;
}
